import { AlertTriangle, Columns2, GripVertical, List, Star } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTemplates } from '@/api/hooks/use-templates'
import type { WpArticle } from '@/api/hooks/use-wordpress'
import type { ArticleForRender } from '@/lib/newsletter-template'
import { renderFullNewsletter } from '@/lib/newsletter-template'

interface Props {
  articles: WpArticle[]
  onArticlesReorder: (reordered: WpArticle[]) => void
  templateId: string
  onTemplateIdChange: (v: string) => void
  campaignName: string
  onBack: () => void
  onNext: () => void
}

const MAX_ARTICLES = 10

function positionLabel(i: number): { icon: typeof Star; label: string } {
  if (i === 0) return { icon: Star, label: 'Featured' }
  if (i <= 2) return { icon: Columns2, label: 'Column' }
  return { icon: List, label: 'Compact' }
}

export function CampaignStepLayout({
  articles,
  onArticlesReorder,
  templateId,
  onTemplateIdChange,
  campaignName,
  onBack,
  onNext,
}: Props): React.JSX.Element {
  const { data: templateListData } = useTemplates()
  const templates = templateListData?.data ?? []
  const [dragIdx, setDragIdx] = useState<number | null>(null)
  const [overIdx, setOverIdx] = useState<number | null>(null)
  const dragNode = useRef<HTMLDivElement | null>(null)

  // Validation
  const errors: string[] = []
  if (articles.length === 0)
    errors.push('No articles to display. Go back and select an article group.')
  if (articles.length > MAX_ARTICLES)
    errors.push(
      `Too many articles (${articles.length}). The template supports up to ${MAX_ARTICLES}. Remove ${articles.length - MAX_ARTICLES} article${articles.length - MAX_ARTICLES > 1 ? 's' : ''} from your group.`,
    )

  const displayArticles = articles.slice(0, MAX_ARTICLES)
  const hasWarning = articles.length > MAX_ARTICLES
  const canProceed = errors.length === 0 && displayArticles.length > 0

  const toRender: ArticleForRender[] = displayArticles.map((a) => ({
    title: a.title,
    excerpt: a.excerpt,
    cover_image_url: a.cover_image_url,
    author: a.author,
    permalink: a.permalink,
    categories: a.categories,
  }))

  let previewHtml: string
  try {
    previewHtml = renderFullNewsletter(
      toRender,
      campaignName || 'Your Newsletter',
      new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
    )
  } catch {
    previewHtml =
      '<p style="color:red;text-align:center;padding:40px;">Failed to render preview. Check your articles.</p>'
    if (errors.length === 0)
      errors.push('Failed to render the newsletter. Try removing some articles.')
  }

  function handleDragStart(idx: number, e: React.DragEvent<HTMLDivElement>): void {
    setDragIdx(idx)
    dragNode.current = e.currentTarget
    e.dataTransfer.effectAllowed = 'move'
    requestAnimationFrame(() => {
      if (dragNode.current) dragNode.current.style.opacity = '0.4'
    })
  }

  function handleDragEnd(): void {
    if (dragNode.current) dragNode.current.style.opacity = '1'
    setDragIdx(null)
    setOverIdx(null)
    dragNode.current = null
  }

  function handleDragOver(idx: number, e: React.DragEvent): void {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (idx !== overIdx) setOverIdx(idx)
  }

  function handleDrop(dropIdx: number, e: React.DragEvent): void {
    e.preventDefault()
    if (dragIdx === null || dragIdx === dropIdx) return
    const reordered = [...articles]
    const [moved] = reordered.splice(dragIdx, 1)
    if (!moved) return
    reordered.splice(dropIdx, 0, moved)
    onArticlesReorder(reordered)
    setDragIdx(null)
    setOverIdx(null)
  }

  return (
    <div className="flex gap-6" style={{ maxWidth: 1100, margin: '0 auto' }}>
      {/* Sidebar */}
      <div className="w-72 shrink-0 space-y-3">
        <p className="text-sm font-medium text-text-primary">Article order</p>
        <p className="text-xs text-text-secondary">
          Drag to reorder. Position determines layout. Max {MAX_ARTICLES} articles.
        </p>

        {/* Warnings/errors */}
        {hasWarning && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <p className="text-xs text-amber-700">
              Your group has {articles.length} articles but the template supports {MAX_ARTICLES}.
              The last {articles.length - MAX_ARTICLES} will be excluded.
            </p>
          </div>
        )}
        {articles.length === 0 && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-2.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
            <p className="text-xs text-red-700">
              No articles in this group. Go back and select a group with articles.
            </p>
          </div>
        )}

        <div className="space-y-1">
          {articles.map((a, i) => {
            const pos = positionLabel(i)
            const Icon = pos.icon
            const isDragging = dragIdx === i
            const isOver = overIdx === i && dragIdx !== i
            const isExcluded = i >= MAX_ARTICLES

            return (
              <div
                key={a.id}
                role="option"
                aria-selected={false}
                tabIndex={0}
                draggable
                onDragStart={(e) => handleDragStart(i, e)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(i, e)}
                onDrop={(e) => handleDrop(i, e)}
                className={`flex items-center gap-2 rounded-lg border p-2 text-sm transition-all cursor-grab active:cursor-grabbing ${
                  isExcluded
                    ? 'border-red-200 bg-red-50 opacity-50'
                    : isDragging
                      ? 'border-brand-300 bg-brand-50 opacity-40'
                      : isOver
                        ? 'border-brand-500 bg-brand-50 shadow-sm'
                        : 'border-surface-3 bg-surface-0 hover:bg-surface-1'
                }`}
              >
                <GripVertical className="h-4 w-4 shrink-0 text-text-tertiary" />
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded text-xs font-medium ${
                    isExcluded ? 'bg-red-100 text-red-500' : 'bg-surface-2 text-text-secondary'
                  }`}
                >
                  {i + 1}
                </span>
                {a.cover_image_url && (
                  <img
                    src={a.cover_image_url}
                    alt=""
                    className="h-8 w-10 shrink-0 rounded object-cover"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium text-text-primary">{a.title}</p>
                  <div className="flex items-center gap-1 text-[10px] text-text-tertiary">
                    <Icon className="h-3 w-3" />
                    {isExcluded ? 'Excluded' : pos.label}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <div className="border-t border-surface-3 pt-3">
          <label htmlFor="layout-tpl" className="block text-xs font-medium text-text-secondary">
            Template
          </label>
          <select
            id="layout-tpl"
            value={templateId}
            onChange={(e) => onTemplateIdChange(e.target.value)}
            className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 px-2 py-1.5 text-xs text-text-primary focus:border-brand-500 focus:outline-none"
          >
            <option value="">Newspaper (default)</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} (v{t.version})
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-2 pt-2">
          <button
            type="button"
            onClick={onBack}
            className="w-full rounded-lg border border-surface-3 px-4 py-2 text-sm font-medium text-text-primary hover:bg-surface-1"
          >
            ← Back
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={!canProceed}
            className="w-full rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-40"
          >
            Next: Review →
          </button>
        </div>
      </div>

      {/* Main — live preview */}
      <div className="min-w-0 flex-1">
        <p className="mb-2 text-sm font-medium text-text-primary">
          Live preview
          {hasWarning && (
            <span className="ml-2 text-xs text-amber-600">
              (showing first {MAX_ARTICLES} of {articles.length})
            </span>
          )}
        </p>
        <div
          className="overflow-auto rounded-xl border border-surface-3 bg-[#f8f7f4] shadow-inner"
          style={{ maxHeight: 'calc(100vh - 220px)' }}
        >
          <div
            className="mx-auto"
            style={{ maxWidth: 620, padding: '10px' }}
            // biome-ignore lint/security/noDangerouslySetInnerHtml: intentional for rendered HTML content
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </div>
      </div>
    </div>
  )
}
